import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material';
import {
  Refresh,
  Search,
  Download,
  Visibility,
  Close
} from '@mui/icons-material';
import { getFormSubmissions, getFormStatistics } from '../services/api';

interface FormSubmissionRecord {
  id: string;
  taskId?: string;
  formTemplateId: string;
  formTemplate?: {
    id: string;
    name: string;
  };
  data: Record<string, any>;
  validationErrors?: any;
  status: 'pending' | 'completed' | 'failed';
  submittedBy?: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface FormStats {
  total: number;
  byStatus: Record<string, number>;
  successRate: string;
}

const FormSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<FormSubmissionRecord[]>([]);
  const [stats, setStats] = useState<FormStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmissionRecord | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterForm, setFilterForm] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (filterStatus !== 'all') {
        filters.status = filterStatus;
      }
      if (filterForm !== 'all') {
        filters.formTemplateId = filterForm;
      }

      const [submissionsRes, statsRes] = await Promise.all([
        getFormSubmissions(filters),
        getFormStatistics()
      ]);

      setSubmissions(submissionsRes.data?.submissions || []);
      setStats(statsRes.data?.statistics || null);
    } catch (err: any) {
      console.error('Failed to fetch submissions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterForm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetails = (submission: FormSubmissionRecord) => {
    setSelectedSubmission(submission);
    setViewDialogOpen(true);
  };

  const handleExport = () => {
    const filtered = getFilteredSubmissions();
    const csv = [
      ['ID', 'Form Name', 'Submitted By', 'Submitted At', 'Status'],
      ...filtered.map(s => [
        s.id,
        s.formTemplate?.name || s.formTemplateId,
        s.submittedBy || '',
        new Date(s.createdAt).toLocaleString(),
        s.status
      ])
    ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getFilteredSubmissions = () => {
    if (!searchTerm) return submissions;
    const term = searchTerm.toLowerCase();
    return submissions.filter(sub =>
      (sub.submittedBy?.toLowerCase().includes(term)) ||
      (sub.formTemplate?.name?.toLowerCase().includes(term)) ||
      sub.id.toLowerCase().includes(term)
    );
  };

  const uniqueForms = Array.from(
    new Map(
      submissions
        .filter(s => s.formTemplate)
        .map(s => [s.formTemplate!.id, s.formTemplate!.name])
    ).entries()
  );

  const filteredSubmissions = getFilteredSubmissions();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Form Submissions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search submissions..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Form</InputLabel>
            <Select
              value={filterForm}
              label="Form"
              onChange={(e) => setFilterForm(e.target.value)}
            >
              <MenuItem value="all">All Forms</MenuItem>
              {uniqueForms.map(([id, name]) => (
                <MenuItem key={id} value={id}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={filteredSubmissions.length === 0}
          >
            Export CSV
          </Button>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Submissions
            </Typography>
            <Typography variant="h4">
              {stats?.total ?? submissions.length}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" color="warning.main">
              {stats?.byStatus?.pending ?? 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Completed
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats?.byStatus?.completed ?? 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Failed
            </Typography>
            <Typography variant="h4" color="error.main">
              {stats?.byStatus?.failed ?? 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Submissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Form Name</TableCell>
              <TableCell>Submitted By</TableCell>
              <TableCell>Submitted At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSubmissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No submissions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSubmissions.map((submission) => (
                <TableRow key={submission.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {submission.formTemplate?.name || 'Unknown Form'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {submission.taskId ? `Task: ${submission.taskId.slice(0, 8)}...` : 'No task'}
                    </Typography>
                  </TableCell>
                  <TableCell>{submission.submittedBy || '—'}</TableCell>
                  <TableCell>
                    {new Date(submission.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={submission.status}
                      color={getStatusColor(submission.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(submission)}
                      title="View Details"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedSubmission && (
          <>
            <DialogTitle>
              Submission Details
              <IconButton
                onClick={() => setViewDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedSubmission.formTemplate?.name || 'Unknown Form'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip
                    label={selectedSubmission.status}
                    color={getStatusColor(selectedSubmission.status) as any}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Submitted by: {selectedSubmission.submittedBy || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submitted at: {new Date(selectedSubmission.createdAt).toLocaleString()}
                </Typography>
                {selectedSubmission.taskId && (
                  <Typography variant="body2" color="text.secondary">
                    Task ID: {selectedSubmission.taskId}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Submission ID: {selectedSubmission.id}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Form Data
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                {Object.entries(selectedSubmission.data).map(([key, value]) => (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </Typography>
                    <Typography variant="body2">
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Paper>

              {selectedSubmission.validationErrors && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Validation Errors
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedSubmission.validationErrors, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default FormSubmissions;
